import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { tailorsApi } from '../api';
import toast from 'react-hot-toast';
import ReceiptTailor from "../components/common/ReceiptTailor";

function EditTailorModal({ tailor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: tailor?.name || '',
    phone: tailor?.phone || '',
    email: tailor?.email || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await tailorsApi.update(tailor.id, form);
      toast.success('Tailor updated successfully!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update tailor');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Tailor</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input type="text" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input type="text" className="form-control" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TailorsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tailorData, setTailorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);

  const getFrequencyLabel = (freq) => {
    if (freq === 'weekly') return 'Weekly';
    if (freq === 'biweekly') return 'Bi-Weekly';
    if (freq === 'monthly') return 'Monthly';
    return 'Weekly';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!tailorData || !tailorData.tailor) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">✂️</div>
          <p>Tailor not found</p>
          <Link to="/tailors" className="btn btn-primary">Back to Tailors</Link>
        </div>
      </div>
    );
  }

  const { tailor, assignments, stats } = tailorData;

  // Separate active and completed assignments
  const activeAssignments = assignments?.filter(a => !a.isCompleted) || [];
  const completedAssignments = assignments?.filter(a => a.isCompleted) || [];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/tailors" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {tailor.name}
            </h1>
            <span className={`badge ${tailor.isActive ? 'badge-active' : 'badge-inactive'}`}>
              {tailor.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Tailor Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Tailor Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value font-semibold">{tailor.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{tailor.phone || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{tailor.email || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className={`info-value badge ${tailor.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {tailor.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Payment Cycle</span>
                <span className="info-value">
                  <span className="badge" style={{ background: 'var(--accent)', color: 'white' }}>
                    {getFrequencyLabel(tailor.settlementFrequency)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Active Assignments Card */}
          <div className="card p-2">
            <h3 className="card-title">Active Assignments ({activeAssignments.length})</h3>
            {activeAssignments.length > 0 ? (
              <div className="orders-list">
                {activeAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="customer-order-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/orders/${assignment.orderItem?.order?.id}`)}
                  >
                    <div className="customer-info">
                      <span className="customer-name">
                        {assignment.orderItem?.order?.customer?.name || 'Unknown Customer'}
                      </span>
                      <span className="order-number">
                        {assignment.orderItem?.order?.orderNumber}
                      </span>
                    </div>
                    <div className="cloth-info">
                      <span className="cloth-quantity">{assignment.orderItem?.garmentType}</span>
                      {getStatusBadge(assignment.isCompleted)}
                    </div>
                    <div className="assignment-details">
                      <span className="text-xs text-muted">
                        Labor: ₹{Number(assignment.laborCost).toLocaleString('en-IN')}
                      </span>
                      <span className="text-xs text-muted">
                        Assigned: {formatDate(assignment.assignedAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                No active assignments
              </div>
            )}
          </div>

          {/* Completed Assignments Card */}
          {completedAssignments.length > 0 && (
            <div className="card p-2">
              <h3 className="card-title">Completed Assignments ({completedAssignments.length})</h3>
              <div className="orders-list">
                {completedAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="customer-order-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/orders/${assignment.orderItem?.order?.id}`)}
                  >
                    <div className="customer-info">
                      <span className="customer-name">
                        {assignment.orderItem?.order?.customer?.name || 'Unknown Customer'}
                      </span>
                      <span className="order-number">
                        {assignment.orderItem?.order?.orderNumber}
                      </span>
                    </div>
                    <div className="cloth-info">
                      <span className="cloth-quantity">{assignment.orderItem?.garmentType}</span>
                      {getStatusBadge(assignment.isCompleted)}
                    </div>
                    <div className="assignment-details">
                      <span className="text-xs text-muted">
                        Labor: ₹{Number(assignment.laborCost).toLocaleString('en-IN')}
                      </span>
                      <span className="text-xs text-muted">
                        Completed: {formatDate(assignment.completedAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Quick Stats Card */}
          <div className="card p-2">
            <h3 className="card-title">Performance Statistics</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Tailor ID</span>
                <span className="mono text-muted">#{tailor.id}</span>
              </div>
              <div className="summary-row">
                <span>Active Assignments</span>
                <span className="font-semibold" style={{ color: 'var(--warning)' }}>{stats?.active || 0}</span>
              </div>
              <div className="summary-row">
                <span>Completed Assignments</span>
                <span className="font-semibold" style={{ color: 'var(--success)' }}>{stats?.completed || 0}</span>
              </div>
              <div className="summary-row total">
                <span>Total Earned</span>
                <span className="font-bold text-success">
                  ₹{Number(stats?.totalEarned || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Completion Rate Card */}
          <div className="card p-2">
            <h3 className="card-title">Work Summary</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Total Assignments</span>
                <span className="font-semibold">{assignments?.length || 0}</span>
              </div>
              <div className="summary-row">
                <span>Completion Rate</span>
                <span className="font-semibold">
                  {assignments?.length > 0
                    ? Math.round((stats?.completed || 0) / assignments.length * 100)
                    : 0}%
                </span>
              </div>
            </div>
            {assignments?.length > 0 && (
              <div className="mt-4">
                <div className="progress-bar">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${(stats?.completed || 0) / assignments.length * 100}%` }}
                  />
                </div>
                <div className="text-xs text-muted mt-1 text-right">
                  {Math.round((stats?.completed || 0) / assignments.length * 100)}% completion rate
                </div>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div className="card p-2">
            <h3 className="card-title">Actions</h3>
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-primary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => setEditModal(true)}
              >
                ✏️ Edit Tailor
              </button>
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/tailors')}
              >
                ← Back to Tailors
              </button>
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/orders`)}
              >
                📋 View All Orders
              </button>
          </div>
        </div>
      </div>

      {editModal && (
        <EditTailorModal
          tailor={tailor}
          onClose={() => setEditModal(false)}
          onSaved={() => fetchTailor()}
        />
      )}
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        .badge-active { background: #d1fae5; color: #065f46; }
        .badge-inactive { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }

        /* Orders List Styles */
        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .customer-order-item {
          background: var(--bg-secondary);
          border-radius: var(--radius);
          padding: 12px;
          transition: all 0.2s;
        }
        .customer-order-item:hover {
          background: var(--border-color);
        }
        .customer-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .customer-name {
          font-weight: 600;
          font-size: 0.9rem;
        }
        .order-number {
          font-size: 0.8rem;
          color: var(--accent-light);
          font-family: monospace;
        }
        .cloth-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cloth-quantity {
          font-size: 0.85rem;
          color: var(--text-muted);
        }
        .assignment-details {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid var(--border-color);
        }
        .mt-4 {
          margin-top: 16px;
        }
        .text-right {
          text-align: right;
        }
      `}</style>
    </div>
  );
}
